import React, { useState } from 'react';
import { Typography, Box, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useTreeStyles = makeStyles((theme) => ({
  treeContainer: {
    maxHeight: '300px',
    overflow: 'auto',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  treeItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.25, 0),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.selected': {
      backgroundColor: theme.palette.primary.light,
      borderLeft: `3px solid ${theme.palette.primary.main}`,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      },
    },
  },
  expandIcon: {
    fontSize: 16,
    marginRight: theme.spacing(0.5),
    cursor: 'pointer',
    userSelect: 'none',
    minWidth: 20,
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  projectIcon: {
    fontSize: 18,
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  projectName: {
    fontWeight: 500,
    flex: 1,
  },
  projectKind: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

export type TeamcityProjectT = {
  id: string;
  name: string;
  parentProjectId: string;
  description: string;
  href: string;
  webUrl: string;
};

export type HierarchicalProject = TeamcityProjectT & {
  children: HierarchicalProject[];
  level: number;
  expanded?: boolean;
};

// Function to build hierarchical structure
export const buildHierarchy = (projects: TeamcityProjectT[]): HierarchicalProject[] => {
  if (!projects || projects.length === 0) {
    return [];
  }

  const projectMap = new Map<string, HierarchicalProject>();
  const roots: HierarchicalProject[] = [];

  projects.forEach(project => {
    projectMap.set(project.id, {
      ...project,
      children: [],
      level: 0,
      expanded: false,
    });
  });

  projects.forEach(project => {
    const currentProject = projectMap.get(project.id)!;
    
    if (project.id === '_Root' || project.name === '_Root') {
      return;
    }
    
    if (project.parentProjectId && project.parentProjectId !== '_Root' && projectMap.has(project.parentProjectId)) {
      const parent = projectMap.get(project.parentProjectId)!;
      parent.children.push(currentProject);
    } else {
      roots.push(currentProject);
    }
  });

  const setLevels = (projectList: HierarchicalProject[], level: number = 0) => {
    projectList.forEach(project => {
      project.level = level;
      setLevels(project.children, level + 1);
    });
  };

  setLevels(roots);

  return roots;
};

// Component for rendering a tree node with catalog hierarchy style
const TreeNode: React.FC<{
  project: HierarchicalProject;
  selectedId: string | undefined;
  onSelect: (project: HierarchicalProject, isAlreadySelected?: boolean) => void;
  onToggle: (projectId: string) => void;
  classes: any;
}> = ({ project, selectedId, onSelect, onToggle, classes }) => {
  const hasChildren = project.children.length > 0;
  const isSelected = project.id === selectedId;
  const paddingLeft = project.level * 20 + 10;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(project, isSelected);
    }
  };

  const handleToggleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle(project.id);
    }
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        className={`${classes.treeItem} ${isSelected ? 'selected' : ''}`}
        style={{ paddingLeft: `${paddingLeft}px` }}
        onClick={() => onSelect(project, isSelected)}
        onKeyDown={handleKeyDown}
      >
        <div
          className={classes.expandIcon}
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) {
              // Only toggle, don't select on icon click
              onToggle(project.id);
            }
          }}
          onKeyDown={handleToggleKeyDown}
          role="button"
          tabIndex={0}
        >
          {hasChildren && project.expanded && '📂'}
          {hasChildren && !project.expanded && '📁'}
          {!hasChildren && '📄'}
        </div>
        
        <Box style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Typography variant="body2" className={classes.projectName}>
            {project.name}
          </Typography>
          <Typography variant="caption" className={classes.projectKind}>
            {hasChildren ? `Parent Project (${project.children.length})` : 'Project'}
          </Typography>
        </Box>
      </div>
      
      {hasChildren && project.expanded && (
        <div>
          {project.children.map(child => (
            <TreeNode
              key={child.id}
              project={child}
              selectedId={selectedId}
              onSelect={onSelect}
              onToggle={onToggle}
              classes={classes}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export interface TeamcityProjectTreeViewProps {
  projects: HierarchicalProject[];
  selectedProject: HierarchicalProject | null;
  onProjectSelect: (project: HierarchicalProject) => void;
  loading?: boolean;
  error?: string;
}

export const TeamcityProjectTreeView: React.FC<TeamcityProjectTreeViewProps> = ({
  projects,
  selectedProject,
  onProjectSelect,
  loading = false,
  error,
}) => {
  const classes = useTreeStyles();
  const [internalProjects, setInternalProjects] = useState<HierarchicalProject[]>(projects);

  React.useEffect(() => {
    setInternalProjects(projects);
  }, [projects]);

  const findProjectInTree = (root: HierarchicalProject, targetId: string): HierarchicalProject | null => {
    if (root.id === targetId) return root;
    for (const child of root.children) {
      const found = findProjectInTree(child, targetId);
      if (found) return found;
    }
    return null;
  };

  const handleToggle = (projectId: string) => {
    const toggleExpanded = (projectList: HierarchicalProject[]): HierarchicalProject[] => {
      return projectList.map(project => {
        if (project.id === projectId) {
          return { ...project, expanded: !project.expanded };
        }
        if (project.children.length > 0) {
          return { ...project, children: toggleExpanded(project.children) };
        }
        return project;
      });
    };
    setInternalProjects(toggleExpanded(internalProjects));
  };

  const handleSelect = (project: HierarchicalProject, isAlreadySelected: boolean = false) => {
    onProjectSelect(project);
    
    let actualProject: HierarchicalProject | null = null;
    for (const rootProject of internalProjects) {
      actualProject = findProjectInTree(rootProject, project.id);
      if (actualProject) break;
    }
    
    if (project.children.length > 0 && actualProject) {
      if (isAlreadySelected) {
        handleToggle(project.id);
      } else {
        if (!actualProject.expanded) {
          handleToggle(project.id);
        }
      }
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" className={classes.treeContainer}>
        <Box className={classes.emptyState}>
          <Typography variant="body1">Loading TeamCity projects...</Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" className={classes.treeContainer}>
        <Box className={classes.emptyState}>
          <Typography variant="body1" color="error">Error: {error}</Typography>
        </Box>
      </Paper>
    );
  }

  if (!internalProjects || internalProjects.length === 0) {
    return (
      <Paper variant="outlined" className={classes.treeContainer}>
        <Box className={classes.emptyState}>
          <Typography variant="body1">No projects found</Typography>
          <Typography variant="body2">
            Try refreshing or check your permissions
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" className={classes.treeContainer}>
      {internalProjects.map(project => (
        <TreeNode
          key={project.id}
          project={project}
          selectedId={selectedProject?.id}
          onSelect={handleSelect}
          onToggle={handleToggle}
          classes={classes}
        />
      ))}
    </Paper>
  );
};